import { supabase } from '../supabaseClient';
import { getRecentlyViewed, recordView as recordViewLocal } from './recentlyViewed';

// Hybrid viewing-history layer. Single source of truth that the rest of the
// app calls.
//
// Storage policy:
//   - Always write to localStorage. It powers the logged-out fallback AND
//     keeps a copy that survives logout, so the next session restores history.
//   - If a user is logged in, also UPSERT into public.listing_views so the
//     account carries history across devices. The DB read takes precedence
//     when it has rows, otherwise we fall back to localStorage.
//
// All Supabase calls are wrapped in try/catch — DB errors degrade silently
// to the localStorage path so the UI never sees a thrown promise.

const DB_LIMIT = 10;

// Tracks which user id we have already merged for in this tab session, so
// AuthContext can call mergeLocalIntoAccount() on every render without
// re-doing the work. Reset on logout (resetMergeGuard) or when a different
// user signs in.
let mergedForUserId = null;

export async function recordView(listing, user) {
  if (!listing?.id) return;

  // localStorage first: synchronous, never fails, populates the logged-out
  // fallback and survives logout.
  recordViewLocal(listing);

  if (!user?.id) return;

  try {
    await supabase
      .from('listing_views')
      .upsert(
        {
          user_id:    user.id,
          listing_id: listing.id,
          viewed_at:  new Date().toISOString(),
        },
        { onConflict: 'user_id,listing_id' }
      );
  } catch {
    // localStorage already captured it.
  }
}

// Returns a de-duplicated, recency-ordered array of listing IDs.
// Preference order:
//   1. Logged in + DB returns rows → DB
//   2. Otherwise → localStorage
// Never throws; on DB error falls through to localStorage.
export async function getViewedListingIds(user) {
  if (user?.id) {
    try {
      const { data, error } = await supabase
        .from('listing_views')
        .select('listing_id, viewed_at')
        .eq('user_id', user.id)
        .order('viewed_at', { ascending: false })
        .limit(DB_LIMIT);

      if (!error && data && data.length > 0) {
        const seen = new Set();
        const ids = [];
        for (const row of data) {
          if (row?.listing_id != null && !seen.has(row.listing_id)) {
            seen.add(row.listing_id);
            ids.push(row.listing_id);
          }
        }
        return ids;
      }
    } catch {
      // fall through to localStorage
    }
  }

  const local = getRecentlyViewed();
  const seen = new Set();
  const ids = [];
  for (const v of local) {
    if (v?.id != null && !seen.has(v.id)) {
      seen.add(v.id);
      ids.push(v.id);
    }
  }
  return ids;
}

// On login, replay localStorage history into the user's listing_views so a
// logged-out browsing session carries into the account. Idempotent per user
// per tab session — safe to call from a render-frequent useEffect.
export async function mergeLocalIntoAccount(user) {
  if (!user?.id) return;
  if (mergedForUserId === user.id) return;

  // Optimistically mark as merged BEFORE the network call. If it fails we'll
  // try again next login (resetMergeGuard runs on logout). This avoids a
  // thundering herd of identical upserts if the same effect re-fires.
  mergedForUserId = user.id;

  const local = getRecentlyViewed();
  if (local.length === 0) return;

  try {
    const rows = local
      .filter(v => v?.id != null)
      .map(v => ({
        user_id:    user.id,
        listing_id: v.id,
        viewed_at:  new Date(v.ts || Date.now()).toISOString(),
      }));

    if (rows.length > 0) {
      await supabase
        .from('listing_views')
        .upsert(rows, { onConflict: 'user_id,listing_id' });
    }
  } catch {
    // localStorage still works as the fallback; nothing to surface.
  }
}

// On logout, allow the next login (possibly a different account) to merge
// its own history fresh.
export function resetMergeGuard() {
  mergedForUserId = null;
}
